import { useEffect, useRef } from 'react';

/**
 * Custom hook for creating a rotating glow effect on buttons
 * Uses CSS custom properties and requestAnimationFrame for smooth animation
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.speed - Animation speed (angle increment per frame, default: 1)
 * @param {boolean} options.enabled - Whether animation is enabled (default: true)
 * @returns {React.RefObject} - Ref to attach to the button element
 */
export function useRotatingGlow({ speed = 1, enabled = true } = {}) {
  const elementRef = useRef(null);

  useEffect(() => {
    if (!enabled || !elementRef.current) return;

    let angle = 0;
    let animationFrameId = null;

    const rotate = () => {
      if (!elementRef.current) return;
      
      angle = (angle + speed) % 360;
      elementRef.current.style.setProperty('--glow-angle', `${angle}deg`);
      animationFrameId = requestAnimationFrame(rotate);
    };

    // Start animation
    animationFrameId = requestAnimationFrame(rotate);

    // Cleanup on unmount
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [enabled, speed]);

  return elementRef;
}
